import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { EMPTY, finalize, of, switchMap, take } from 'rxjs';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { NotificationService } from '@progress/kendo-angular-notification';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { ToastService } from '../../services/toast/toast.service';
import { FormErrorComponent } from '../../shared/components/form-error/form-error.component';
import { CustomerService } from '../../customers/services/customer.service';
import { AppsService } from '../../services/apps/apps.service';
import { CardComponent } from '../../card/card.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'af-cloud-license-server-dashboard',
  imports: [
    ButtonModule,
    LabelModule,
    TextBoxModule,
    CardComponent,
    FormErrorComponent,
    TranslatePipe,
  ],
  templateUrl: './cloud-license-server-dashboard.component.html',
  styleUrl: './cloud-license-server-dashboard.component.scss',
  providers: [NotificationService, ToastService],
})
export class CloudLicenseServerDashboardComponent implements OnInit {
  @ViewChild('successAllowedIpTemplate') successAllowedIpTemplate!: TemplateRef<unknown>;

  public readonly licenseServerAddress = signal<string | undefined>(undefined);
  public readonly errorMessage = signal('');
  public readonly hasCustomerId = signal(false);
  public readonly isLoading = signal(true);
  public readonly isAllowingIp = signal(false);

  constructor(
    private customerService: CustomerService,
    private appsService: AppsService,
    private authenticationService: AuthenticationService,
    private toastService: ToastService
  ) {}

  protected allowedIp: string = ''

  ngOnInit() {
    this.loadLicenseServerAddress();
  }

  public allowAccessFromCurrentIp() {
    this.errorMessage.set('');
    this.isAllowingIp.set(true);
    this.allowedIp = '';

    this.authenticationService
      .getCustomerId()
      .pipe(
        switchMap((customerId) => {
          if (!customerId) {
            throw new Error('CustomerId not found');
          }

          return this.customerService.addTemporaryIngress(customerId);
        }),
        take(1),
        finalize(() => this.isAllowingIp.set(false))
      )
      .subscribe({
        next: ({ allowedIp }) => {
          this.allowedIp = allowedIp;
          this.toastService.showSuccessMessage(this.successAllowedIpTemplate);
        },
        error: (error: unknown) => {
          this.allowedIp = '';
          if (error instanceof HttpErrorResponse) {
            this.errorMessage.set(error.error?.errorMessage ?? error.message);
            return;
          }

          if (error instanceof Error) {
            this.errorMessage.set(error.message);
            return;
          }

          this.errorMessage.set('CLOUD-LICENSE-SERVER.DASHBOARD.MESSAGES.ERROR-FAILED-TO-ALLOW-IP');
        },
      });
  }

  private loadLicenseServerAddress() {
    this.errorMessage.set('');

    this.authenticationService
      .getCustomerId()
      .pipe(
        switchMap((customerId) => {
          this.hasCustomerId.set(Boolean(customerId));

          if (!customerId) {
            this.licenseServerAddress.set(undefined);
            return EMPTY;
          }

          return this.appsService.getAclsApplicationUrl();
        }),
        take(1),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (licenseServerUrl) => {
          this.licenseServerAddress.set(licenseServerUrl);
        },
        error: (error: unknown) => {
          this.licenseServerAddress.set(undefined);

          if (error instanceof HttpErrorResponse) {
            this.errorMessage.set(error.error?.errorMessage ?? error.message);
            return;
          }

          if (error instanceof Error) {
            this.errorMessage.set(error.message);
            return;
          }

          this.errorMessage.set('CLOUD-LICENSE-SERVER.DASHBOARD.MESSAGES.ERROR-FAILED-TO-LOAD-ADDRESS');
        },
      });
  }
}
